import _ from "lodash";
import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import {
  Dropdown,
  DropdownMenu,
  ButtonDisclosure,
  IconButton,
} from "@toddle-edu/ds-web";
import {
  AddCircleOutlined,
  AssessmentApplicableOutlined,
} from "@toddle-edu/ds-icons";
import { PlannerIconsV2 } from "UnitPlans/components/plannerIconsV2";
import update from "immutability-helper";
import { useI18n } from "Hooks";
import { getScoreDetailsMemoize } from "ScoreCategories/modules/ScoreCategoriesModule";
import { getAssessmentDetailsFromCache } from "Assessments/modules/AssessmentGraphqlHelpers";
import {
  ASSESSMENT_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY,
  CURRICULUM_TYPE_DP,
  ASSESSMENT,
  SCORE_BASED_RUBRIC,
  STANDARD_BASED_RUBRIC,
  MYP_OBJECTIVE_RUBRIC,
  MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC,
  SCORE,
  GRADE_SCALE_TOOL,
} from "Constants/stringConstants";
import { ASSESSMENT_TYPE_WORKSHEET } from "Worksheet/modules/WorksheetConstants";
import { getSelectedAcademicYearId } from "modules/CommonSelectors";
import withDerivedProps from "lib/withDerivedProps";
import { compose } from "lib/apolloClient";
import {
  getIsLearningCoursesEnabled,
  getIsSubjectBasedGradingEnabled,
} from "Teacher/routes/LearningCourses/modules/Utils";
import { getFieldValue } from "../../Assessments/modules/Utils";
import { getAssessmentToolItem } from "Utils/AssessmentToolHelpers";
import { ASSESSMENT_TOOLS_OBJ } from "@toddle-edu/common-utils";

const IB_GRADE_SCALE_TOOLS = [
  ASSESSMENT_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY,
];

const incompatibleTools = {
  [SCORE_BASED_RUBRIC]: [SCORE, STANDARD_BASED_RUBRIC], // if score based rubric is added then disable score and standard based rubric
  [STANDARD_BASED_RUBRIC]: [SCORE, SCORE_BASED_RUBRIC], // if standard based rubric is added then disable score and score based rubric
  [SCORE]: [SCORE_BASED_RUBRIC, STANDARD_BASED_RUBRIC], // if score is added then disable assessment grade scale and rubrics
};

const CustomDropdown = props => {
  const {
    options,
    onChangeDropDownMenuOption,
    label,
    curriculumProgramType,
    assessmentId,
    parentType,
    didMount,
    setDidMount,
    assessmentType,
    testId,
    isWorksheetDynamicField = false,
    toggleTooltip = _.noop,
    isDropdownDisabled = false,
    collaboratorsFilter,
    isSubjectBasedGrading,
    assessmentFields,
    gradeScales,
  } = props;

  const { t } = useI18n(["classRoom"]);

  let assessmentTools = props?.assessmentTools || [];

  const [isButtonActive, setIsButtonActive] = useState(false);
  const [isToolLoading, setIsToolLoading] = useState([]);

  // in DP we show comment and score in different section so to disable the dropdown
  // on the basis of remaining tools filter comment and score
  if (curriculumProgramType === CURRICULUM_TYPE_DP) {
    assessmentTools = _.filter(assessmentTools, ({ node }) => {
      const assessmentToolType = _.get(node, "assessmentToolType");
      return !_.includes(["COMMENT", "SCORE"], assessmentToolType);
    });
  }

  // in Worksheet we hide score so to disable the dropdown
  // on the basis of remaining tools filter score
  if (assessmentType == ASSESSMENT_TYPE_WORKSHEET) {
    assessmentTools = _.filter(assessmentTools, ({ node }) => {
      const assessmentToolType = _.get(node, "assessmentToolType");
      return !_.includes(["SCORE"], assessmentToolType);
    });
  }

  const selectedAssessmentTools = _.map(
    assessmentTools,
    ({ node }) => node?.assessmentToolType
  );

  useEffect(() => {
    if (didMount) {
      setIsButtonActive(
        parentType === "QUESTION" && _.isEmpty(selectedAssessmentTools)
      );
    }
    if (setDidMount) {
      setDidMount(false);
    }
  }, []);

  const isDisabled = _.size(selectedAssessmentTools) === _.size(options);

  const { maxScore } = getScoreDetailsMemoize({
    assessmentTools: _.get(
      getAssessmentDetailsFromCache({
        id: assessmentId,
        collaboratorsFilter,
      }),
      "assessmentTools",
      []
    ),
  });

  const updatedOptions = _.map(options, option => {
    const { key, value, options: innerOptions } = option || {};

    let disabled = false;
    const isToolAlreadySelected = _.includes(selectedAssessmentTools, key);

    if (!_.isEmpty(innerOptions)) {
      let disabledTooltip = undefined;

      const updatedInnerOptions = _.map(innerOptions, innerOption => {
        let { key } = innerOption || {};
        const { icon } = innerOption || {};

        let disabled = false;

        if (key === ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY) {
          disabled = _.isEmpty(maxScore);

          if (disabled) {
            disabledTooltip = t("classRoom:define_max_score_to_enable_method");
          }
        } else {
          key = `${key}_${_.toLower(option?.key)}`; // create_new_rubric or use_from_template_rubric
        }

        return {
          ...innerOption,
          key,
          icon: icon ? <PlannerIconsV2 icon={icon} /> : null,
          disabled: isToolAlreadySelected || disabled,
          disabledTooltip,
        };
      });

      if (isToolAlreadySelected) {
        disabled = true;
        disabledTooltip = t("classRoom:assessment_tool_already_added");
      }

      if (key === "IB_GRADE_SCALE") {
        disabled = !_.isEmpty(
          _.intersection(selectedAssessmentTools, IB_GRADE_SCALE_TOOLS)
        );
      } else if ([SCORE_BASED_RUBRIC, STANDARD_BASED_RUBRIC].includes(key)) {
        // Find the first incompatible tool present in selectedAssessmentTools
        const incompatibleTool = _.find(selectedAssessmentTools, tool =>
          incompatibleTools[key].includes(tool)
        );

        if (!_.isEmpty(incompatibleTool) || !_.isEmpty(maxScore)) {
          disabled = true;

          // !_.isEmpty(maxScore) condition is added for DP curriculum as we show SCORE separately in DP
          const label = !_.isEmpty(maxScore)
            ? t("common:score")
            : t(ASSESSMENT_TOOLS_OBJ[incompatibleTool].locale);

          disabledTooltip = t(
            "classRoom:only_one_scoring_based_tool_is_allowed_with_label",
            { label: t("common:lowercase", { text: label }) }
          );
        } else if (isSubjectBasedGrading && key === STANDARD_BASED_RUBRIC) {
          const subjects = getFieldValue({
            fields: assessmentFields,
            uid: "subjects",
          });

          if (_.isEmpty(subjects)) {
            disabled = true;
            disabledTooltip = t(
              "classRoom:tag_a_subject_to_add_standard_rubric"
            );
          }
        }
      }

      return update(option, {
        options: {
          $set: updatedInnerOptions,
        },
        disabled: {
          $set:
            isToolAlreadySelected ||
            disabled ||
            _.includes(isToolLoading, value),
        },
        disabledTooltip: { $set: disabledTooltip },
      });
    } else {
      disabled = false;
      let disabledTooltip = undefined;

      if (isToolAlreadySelected) {
        disabled = true;
        disabledTooltip = t("classRoom:assessment_tool_already_added");
      }

      switch (value) {
        case MYP_OBJECTIVE_RUBRIC:
        case MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC: {
          disabledTooltip = t("classRoom:myp_criteria_not_defined");
          break;
        }
        case SCORE: {
          // Find the first incompatible tool present in selectedAssessmentTools
          const incompatibleTool = _.find(selectedAssessmentTools, tool =>
            incompatibleTools[key].includes(tool)
          );

          if (!_.isEmpty(incompatibleTool)) {
            disabled = true;
            disabledTooltip = t(
              "classRoom:only_one_scoring_based_tool_is_allowed_with_label",
              {
                label: t("common:lowercase", {
                  text: t(ASSESSMENT_TOOLS_OBJ[incompatibleTool].locale),
                }),
              }
            );
          }
          break;
        }

        case GRADE_SCALE_TOOL: {
          if (_.isEmpty(gradeScales)) {
            disabled = true;
            disabledTooltip = t(
              "classRoom:no_grade_scales_added_to_the_course"
            );
          } else {
            const standardBasedRubric = getAssessmentToolItem(
              assessmentTools,
              STANDARD_BASED_RUBRIC
            );
            const gradeScaleId = _.get(standardBasedRubric, "gradeScale.id");

            const standardRubricGradeScale = _.find(gradeScales, {
              gradeScaleId,
            });

            // TODO: @shivam-toddleapp localise and confirm text
            if (gradeScaleId && _.isEmpty(standardRubricGradeScale)) {
              disabled = true;
              // disabledTooltip =
              //   "Grade scale present in standard rubric is not present in grade scale tool";
            }
          }

          if (isSubjectBasedGrading) {
            const subjects = getFieldValue({
              fields: assessmentFields,
              uid: "subjects",
            });

            if (_.isEmpty(subjects)) {
              disabled = true;
              disabledTooltip = t(
                "classRoom:tag_a_subject_to_add_a_with_label",
                { label: t("common:grade_scale") }
              );
            } else if (_.size(subjects) > 1) {
              disabled = true;
              // disabledTooltip = "Please select only one subject";
            }
          }
          break;
        }
      }

      return {
        ...option,
        disabled:
          disabled ||
          _.includes(selectedAssessmentTools, value) ||
          _.includes(isToolLoading, value),
        disabledTooltip,
      };
    }
  });

  const onVisibleChangeHandler = value => {
    if (isWorksheetDynamicField) toggleTooltip(!value);
    setIsButtonActive(value);
  };

  const buttonStyles = isDisabled ? { pointerEvents: "none" } : {};

  const onChange = e => {
    onChangeDropDownMenuOption(e, setIsToolLoading);
  };

  let dropdownButton = null;

  if (parentType === "QUESTION") {
    if (isWorksheetDynamicField) {
      dropdownButton = (
        <IconButton
          dsVersion="2.0"
          _legacy_variant="outlined-subtle"
          variant="neutral"
          type="outlined"
          icon={<AssessmentApplicableOutlined variant="subtle" />}
          size="large"
          testId={testId}
        />
      );
    } else {
      dropdownButton = (
        <IconButton
          icon={<AddCircleOutlined variant="subtle" />}
          size={"medium"}
          _legacy_variant={"outlined-subtle"}
          variant="neutral"
          type="outlined"
          testId={testId}
        />
      );
    }
  } else if (parentType === ASSESSMENT) {
    dropdownButton = (
      <IconButton
        icon={<AddCircleOutlined variant="subtle" />}
        size={"small"}
        _legacy_variant="plain"
        variant={"neutral"}
        type="plain"
        testId={testId}
      />
    );
  } else {
    dropdownButton = (
      <ButtonDisclosure
        icon={<AddCircleOutlined variant="subtle" />}
        size={"medium"}
        _legacy_variant={"outlined-subtle"}
        variant="neutral"
        type="outlined"
        style={buttonStyles}
        testId={testId}
      >
        {label}
      </ButtonDisclosure>
    );
  }

  return (
    <div
      style={{
        display: "inline-block",
        cursor: "not-allowed",
        marginRight: "0",
      }}
    >
      <Dropdown
        overlay={
          <DropdownMenu
            style={{
              maxHeight: "254px",
            }}
            options={updatedOptions}
            onClick={onChange}
            testId={testId}
          />
        }
        visible={isButtonActive}
        onVisibleChange={value => onVisibleChangeHandler(value)}
        disabled={isDisabled || isDropdownDisabled}
      >
        {dropdownButton}
      </Dropdown>
    </div>
  );
};

CustomDropdown.displayName = "CustomDropdown";

const mapStateToProps = state => {
  const academicYearId = getSelectedAcademicYearId(state);
  const collaboratorsFilter = {
    userAcademicYearId: academicYearId,
  };

  return {
    collaboratorsFilter,
  };
};

const getDerivedProps = () => {
  const isSubjectBasedGrading = getIsSubjectBasedGradingEnabled();
  const isLearningCoursesEnabled = getIsLearningCoursesEnabled();

  return {
    isSubjectBasedGrading: !isLearningCoursesEnabled || isSubjectBasedGrading,
  };
};

export default compose(
  connect(mapStateToProps, null),
  withDerivedProps(getDerivedProps)
)(CustomDropdown);
